import { useCallback, useEffect, useRef, useState } from 'react';
import instance from '../../api.config';
import { socket } from '../../socket';

const EMPTY_MESSAGES = [];

// History, HTTP acknowledgements and socket events describe the same saved row.
export function mergeMessages(previous, incoming, chatId) {
    const messages = new Map();
    [...previous, ...incoming].forEach(message => {
        if (message?.id == null || String(message.chatId) !== String(chatId)) return;
        const key = String(message.id);
        messages.set(key, { ...messages.get(key), ...message });
    });
    return [...messages.values()].sort((a, b) => Number(a.id) - Number(b.id));
}

export default function useChatMessages(chatId, userId) {
    const [state, setState] = useState({ chatId: null, messages: EMPTY_MESSAGES, newMsgCount: 0 });
    const activeChatId = useRef(chatId);
    activeChatId.current = chatId;

    const acceptSavedMessage = useCallback(message => {
        if (message?.id == null || String(message.chatId) !== String(activeChatId.current)) return;
        setState(previous => ({
            chatId: message.chatId,
            messages: mergeMessages(previous.messages, [message], message.chatId),
            newMsgCount: String(previous.chatId) === String(message.chatId) ? previous.newMsgCount : 0
        }));
    }, []);

    useEffect(() => {
        let active = true;
        let latestRequest = 0;
        setState({ chatId, messages: EMPTY_MESSAGES, newMsgCount: 0 });
        if (!chatId) return;

        const loadHistory = async () => {
            const request = ++latestRequest;
            try {
                const response = await instance.get('/getMessages/' + chatId);
                if (!active || request !== latestRequest) return;
                setState(previous => ({
                    chatId,
                    messages: mergeMessages(previous.messages, response.data.messages, chatId),
                    newMsgCount: response.data.newMsgCount
                }));
            } catch (error) {
                if (active) console.error(error);
            }
        };

        const receiveMessage = message => {
            if (message?.chatId != null && String(message.chatId) !== String(chatId)) return;
            // Older senders may only announce a change; obtain the saved row instead
            // of inventing an ID or displaying the browser's current time.
            if (message?.chatId == null || message.id == null || !message.createdAt) {
                loadHistory();
                return;
            }
            acceptSavedMessage(message);
        };

        const joinChat = () => {
            socket.emit('addUser', userId);
            socket.emit('joinChat', chatId);
        };
        const reconnect = () => {
            joinChat();
            loadHistory();
        };

        socket.on('getMessage', receiveMessage);
        socket.on('messageCreated', receiveMessage);
        socket.on('supportMessageCreate', receiveMessage);
        socket.on('connect', reconnect);
        joinChat();
        loadHistory();

        return () => {
            active = false;
            socket.off('getMessage', receiveMessage);
            socket.off('messageCreated', receiveMessage);
            socket.off('supportMessageCreate', receiveMessage);
            socket.off('connect', reconnect);
            socket.emit('leaveChat', chatId);
        };
    }, [chatId, userId, acceptSavedMessage]);

    return {
        messages: String(state.chatId) === String(chatId) ? state.messages : EMPTY_MESSAGES,
        newMsgCount: String(state.chatId) === String(chatId) ? state.newMsgCount : 0,
        acceptSavedMessage,
        isCurrentChat: submittedChatId => String(submittedChatId) === String(activeChatId.current)
    };
}
